/**
 * Reschedule an appointment.
 *
 * PATCH /v1/appointments/{id}/reschedule  { startsAt, endsAt, force }
 *
 * Backend rules mirrored here so the user can't submit something that 400s:
 *   • COMPLETED / CANCELLED / CHECKED_IN appointments cannot be rescheduled.
 *   • The consultant does NOT change — the service reuses appt.consultantId.
 *     So the slot picker is scoped to that consultant.
 *   • Conflicts return APPOINTMENT_CONFLICT unless force=true → we offer it.
 *
 * On success the appointment lands on CONFIRMED with a RESCHEDULED hop
 * recorded in its statusHistory.
 */
import { useEffect, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import SlotPicker, { fmtTime } from './SlotPicker';
import { appointmentsService } from '../lib/services';
import { apiErrorMessage, apiErrorCode } from '../lib/apiError';

const NON_RESCHEDULABLE = ['COMPLETED', 'CANCELLED', 'CHECKED_IN'];

const dateOf = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  // Local calendar date (not UTC) — the diary works in local dealership time.
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

export default function RescheduleModal({ open, appointment, onClose, onRescheduled }) {
  const [date, setDate] = useState(dateOf());
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);

  // Reset when the modal opens or a different appointment is targeted — keyed
  // on the appointment ID, NOT the appointment object or onClose. Both of those
  // get fresh identities on every parent re-render (LeadDetail/DealDetail poll
  // every 3s), and depending on them here would blank out the slot the user
  // just picked mid-reschedule.
  useEffect(() => {
    if (!open || !appointment) return;
    setDate(dateOf(appointment.startsAt));
    setSlot(null);
    setBusy(false);
    setError(null);
    setConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment?.id]);

  // Close on Escape — separate so re-registering the listener never resets state.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !appointment) return null;

  const blocked = NON_RESCHEDULABLE.includes(appointment.status);

  const doReschedule = async (force) => {
    if (!slot) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await appointmentsService.reschedule(appointment.id, {
        startsAt: slot.startsAt,
        endsAt:   slot.endsAt,
        ...(force ? { force: true } : {}),
      });
      onRescheduled?.(saved);
      onClose?.();
    } catch (err) {
      if (apiErrorCode(err) === 'APPOINTMENT_CONFLICT') {
        setConflict(true);
        setError('The consultant already has something overlapping that slot.');
      } else {
        setError(apiErrorMessage(err, 'Could not reschedule'));
      }
      setBusy(false);
    }
  };

  const submit = (e) => { e.preventDefault(); doReschedule(false); };

  const currentWhen = appointment.startsAt
    ? `${formatInOrgTz(appointment.startsAt, { weekday: 'short', day: '2-digit', month: 'short' })} · ${fmtTime(appointment.startsAt)}`
    : '—';

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="rsm-title"
         onClick={onClose} className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: 'min(520px, 92vw)' }}>
        <div className="modal-header">
          <div>
            <div id="rsm-title" style={{ fontSize: 16, fontWeight: 600 }}>Reschedule appointment</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {appointment.type?.replace(/_/g, ' ')} · currently {currentWhen}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {blocked ? (
            <div className="text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
              A {appointment.status.toLowerCase().replace('_', ' ')} appointment can't be
              rescheduled. {appointment.status === 'CHECKED_IN'
                ? 'The customer has already arrived.'
                : 'Book a new appointment instead.'}
            </div>
          ) : (
            <>
              <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
                Stays with the same consultant. The appointment is re-confirmed at the
                new time and the move is written to its status history.
              </div>

              <SlotPicker
                date={date}
                onDateChange={(d) => { setDate(d); setSlot(null); setConflict(false); }}
                type={appointment.type}
                consultantId={appointment.consultantId}
                value={slot}
                onSelect={(s) => { setSlot(s); setConflict(false); setError(null); }}
                disabled={busy}
              />
            </>
          )}

          {error && (
            <div className="text-xs" style={{ color: conflict ? 'var(--warning)' : 'var(--danger)', lineHeight: 1.6 }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          {!blocked && (conflict ? (
            <button type="button" className="btn small danger" onClick={() => doReschedule(true)} disabled={busy}>
              {busy ? 'Moving…' : 'Move anyway (double-book)'}
            </button>
          ) : (
            <button type="submit" className="btn small primary" disabled={busy || !slot}>
              {busy ? 'Moving…' : 'Reschedule'}
            </button>
          ))}
        </div>
      </form>
    </div>
  );
}
